<template>
  <div :dir="isRtl != false ? 'rtl' : 'ltr'" :class="['relative w-full agent-type-selector-dropdown-container']">
    <button 
      type="button" 
      class="btn w-full justify-start font-normal text-left h-auto py-2 px-3 min-h-0"
      @click="toggleDropdown"
      aria-haspopup="listbox"
      :aria-expanded="isOpen !== false"
    >
      <div v-if="selectedTypeObject !== null" class="flex items-center space-x-2 w-full">
        <span class="inline-flex justify-center items-center w-7 h-7 rounded-md bg-base-300 dark:bg-gray-700 flex-shrink-0">
          <Icon :icon="selectedTypeObject.icon" class="w-4 h-4 text-base-content" />
        </span>
        <div class="flex-grow overflow-hidden relative w-full" :class="isRtl === true ? 'text-right' : 'text-left'" :style="isRtl === true ? 'padding-right:2%;' : ''">
          <span class="block text-sm font-medium truncate relative w-full" :class="isRtl === true ? 'text-right' : 'text-left'" :style="isRtl === true ? 'padding-right:2%;' : ''">{{ selectedTypeObject.displayName }}</span>
          <span class="block text-xs text-base-content opacity-70 truncate" :class="isRtl === true ? 'text-right' : 'text-left'" :style="isRtl === true ? 'padding-right:2%;' : ''">{{ selectedTypeObject.description }}</span>
        </div>
        <Icon icon="heroicons:chevron-down-20-solid" class="w-5 h-5 text-base-content opacity-60 flex-shrink-0 ml-auto" />
      </div>
      <div v-else class="flex items-center justify-between w-full">
        <span class="text-sm text-base-content opacity-70">{{ placeholderLabel }}</span>
        <Icon icon="heroicons:chevron-down-20-solid" class="w-5 h-5 text-base-content opacity-60" />
      </div>
    </button>

    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div 
        v-if="isOpen === true"
        class="absolute z-10 mt-1 w-full bg-base-100 dark:bg-gray-800 shadow-lg rounded-md border border-base-300 dark:border-gray-700 agent-type-options-list"
        ref="dropdownListRef"
      >
        <div class="py-1 space-y-1">
          <div 
            v-for="typeOption in availableTypes" 
            :key="typeOption.id"
            class="card-as-row mx-1 rounded-md cursor-pointer transition-all duration-150 ease-in-out"
            :class="{ 
              'bg-primary text-primary-content': typeOption.id === selectedAgentType,
              'hover:bg-base-200 dark:hover:bg-gray-700': typeOption.id !== selectedAgentType
            }"
            @click="selectTypeAndClose(typeOption.id)"
          >
            <div class="p-3">
              <div class="flex items-start space-x-4">
                <div class="flex-shrink-0">
                  <span class="inline-flex justify-center items-center w-8 h-8 rounded-lg bg-base-300 dark:bg-gray-600" >
                    <Icon :icon="typeOption.icon" class="w-5 h-5 text-base-content" />
                  </span>
                </div>
                <div class="flex-grow overflow-hidden relative w-full" :class="isRtl === true ? 'text-right' : 'text-left'">
                  <h3 class="text-sm font-semibold leading-tight" :class="[{ 'text-primary-content': typeOption.id === selectedAgentType }, isRtl === true ? 'text-right' : 'text-left']" :style="isRtl === true ? 'padding-right:2%;' : ''">{{ typeOption.displayName }}</h3>
                  <p class="text-xs mt-0.5" :class="[typeOption.id === selectedAgentType ? 'text-primary-content opacity-80' : 'text-base-content opacity-70', isRtl === true ? 'text-right' : 'text-left']" :style="isRtl === true ? 'padding-right:2%;' : ''">
                    {{ typeOption.description }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { Icon } from "@iconify/vue/dist/offline";
import { useI18n } from '@/i18n';

const props = defineProps({
  selectedAgentType: {
    type: String,
    default: 'PublicAgent'
  },
  options: {
    type: Array,
    default: null
  },
  placeholderText: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['update:selectedAgentType']);

const isOpen = ref(false);
const dropdownListRef = ref(null);
const { t, currentLanguage } = useI18n();
const isRtl = computed(() => (['he','ar'].includes(currentLanguage.value) === true) ? true : false);

const defaultAgentTypeOptions = computed(() => [
  {
    id: 'PublicAgent',
    displayName: t('agentTypeSelector.types.publicAgent.displayName'),
    description: t('agentTypeSelector.types.publicAgent.description'),
    icon: 'heroicons:globe-alt-20-solid'
  },
  {
    id: 'PrivateAgent',
    displayName: t('agentTypeSelector.types.privateAgent.displayName'),
    description: t('agentTypeSelector.types.privateAgent.description'),
    icon: 'heroicons:lock-closed-20-solid'
  }
]);

const availableTypes = computed(() => {
  if (props.options !== null && props.options !== undefined && props.options.length > 0) {
    return props.options;
  }
  return defaultAgentTypeOptions.value;
});

const placeholderLabel = computed(() => {
  if (props.placeholderText !== '') {
    return props.placeholderText;
  }
  return t('agentTypeSelector.selectAgentType');
});

const selectedTypeObject = computed(() => {
  const matchedType = availableTypes.value.find(typeOption => typeOption.id === props.selectedAgentType);
  if (matchedType !== undefined && matchedType !== null) {
    return matchedType;
  }
  return null;
});

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

const selectTypeAndClose = (typeId) => {
  if (props.selectedAgentType !== typeId) {
    emit('update:selectedAgentType', typeId);
  }
  isOpen.value = false; 
};

const handleClickOutside = (event) => {
  if (dropdownListRef.value !== null) {
    if (isOpen.value === true) {
      if (dropdownListRef.value.contains(event.target) === false) {
        if (event.target.closest('.agent-type-selector-dropdown-container button[aria-haspopup="listbox"]') === null) {
          isOpen.value = false;
        }
      }
    }
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside, true);
});
</script>

<style scoped>
.agent-type-options-list {
  max-height: 400px; 
  overflow-y: auto;
}

.card-as-row {
}
</style> 